/* eslint-disable no-unused-vars */
/* jshint esversion: 11, module: true, browser: true */
// noinspection ExceptionCaughtLocallyJS,JSUnusedLocalSymbols

import { createRouter, createWebHistory } from 'vue-router';

import { routeToAutomatedSurveyAssignment } from '@/components/utilities/AutomatedSurveyChecker.vue';
import { appTitleSuffix } from '@/Helpers.js';
import { registerChunkLoadErrorHandler, registerPreloadErrorHandler } from '@/router/chunkLoadErrorHandler.js';
import routes from '@/router/ModularRoutes.js';
import { sync } from '@/router/sync.js';
import loadingService from '@/services/LoadingService.js';
import store from '@/store/Index.js';
import { buildAccessDeniedLocation, setPendingNavigationRoute } from '@/utilities/accessDeniedLocation.js';

// The middleware for every page of the application
const globalMiddleware = [
    'CheckAuth',
    'CheckAccountDisabled',
    'ForcePasswordChange',
    'CheckQuizAttempt',
    'CheckIdentityVerificationAttempt',
    'CheckSessionTimeout',
];

// Load middleware modules dynamically
const routeMiddleware = import.meta.glob('@/middleware/*.js', { eager: true });

const PERSONAL_PROFILE_BASE_ROUTES = ['/me', '/my'];

// Base sections shaped like `/<type>/<id>` whose second path segment is a
// static sub-page, NOT a resource id. Without this, the menu-reset heuristic in
// `getBaseResource` reads e.g. `/manage-settings/certificates` or
// `/certificates/manage` as navigation to a brand-new resource and dispatches
// `Menu/reset`, which hides the corporate static sidebar (it has no
// per-resource menu to restore). `certificates` only ever has the static
// `/certificates` and `/certificates/manage` (admin) routes — no
// `/certificates/:id` resource page — so excluding it is safe. Listing the
// section here keeps those sub-pages from being mistaken for resource-detail
// routes. See resources/js/components/sidebar/CorporateSidebar.vue.
const NON_RESOURCE_BASE_SECTIONS = ['manage-settings', 'certificates'];

/**
 * Scroll Behavior
 *
 * @link https://router.vuejs.org/en/advanced/scroll-behavior.html
 *
 * @param  {import('vue-router').Route} to
 * @param  {import('vue-router').Route} from
 * @param  {Object|undefined} savedPosition
 * @return {Object}
 */
function scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
        return savedPosition;
    }

    if (to.hash) {
        return { el: to.hash };
    }

    const matchedComponents = to.matched.slice().reverse();
    const [component] = matchedComponents.map(matched => matched.components.default).slice(-1);

    if (component && component.scrollToTop === false) {
        return {};
    }

    return { left: 0, top: 0 };
}

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior,
});

registerChunkLoadErrorHandler(router);
registerPreloadErrorHandler(router);

/**
 * Replace the current route and skip the middleware.
 * This is useful when you want to redirect the user to another page without
 * running the middleware.
 * @param {*[][]} args
 * @returns {Promise<NavigationFailure | void | undefined>}
 */
router.replaceAndSkipMiddleware = async function replaceAndSkipMiddleware(...args) {
    let middleware;
    let route;

    if (args.length === 2) {
        [middleware, route] = args;
    } else {
        middleware = true;
        [route] = args;
    }

    try {
        router.skipMiddleware = middleware;

        return await router.replace(route);
    } finally {
        router.skipMiddleware = false;
    }
};

/**
 * Merge the the global middleware with the components middleware.
 *
 * @param  {Array} components
 * @return {Array}
 */
async function getMiddleware(components /* Array<Function|Object> */) {
    const filteredGlobalMiddleware = globalMiddleware.filter(middlewareName => {
        if (typeof router.skipMiddleware === 'string') {
            return middlewareName !== router.skipMiddleware;
        }

        if (Array.isArray(router.skipMiddleware)) {
            return !router.skipMiddleware.includes(middlewareName);
        }

        return true;
    });

    const compiledMiddleware = [...filteredGlobalMiddleware];

    // Resolve all components concurrently. Wrap non-functions in resolved promises.
    const resolvedComponentPromises = components.map(component =>
        typeof component === 'function' ? component() : Promise.resolve({ default: component })
    );

    const resolvedComponents = await Promise.all(resolvedComponentPromises);

    // Extract middleware from the resolved components
    resolvedComponents.forEach(resolved => {
        const middleware = resolved.default?.middleware;
        if (middleware) {
            const middlewareToAdd = Array.isArray(middleware) ? middleware : [middleware];
            compiledMiddleware.push(...middlewareToAdd);
        }
    });

    return compiledMiddleware;
}

/**
 * Call each middleware.
 *
 * @param {Array} middleware
 * @param {import('vue-router').Route} to
 * @param {import('vue-router').Route} from
 * @param {Function} next
 */
async function callMiddleware(middleware, to, from, next) {
    const stack = middleware.reverse();

    const processNextMiddleware = async wasNextCalled => {
        if (stack.length === 0 || wasNextCalled) {
            return;
        }

        const nextMiddleware = stack.pop();

        try {
            let middlewareFunction;

            if (typeof nextMiddleware === 'function') {
                middlewareFunction = nextMiddleware;
            } else {
                middlewareFunction = routeMiddleware[`/resources/js/middleware/${nextMiddleware}.js`]?.default;
            }

            if (!middlewareFunction) {
                throw new Error(`Undefined middleware [${nextMiddleware}]`);
            }

            await middlewareFunction(to, from, async (...args) => {
                if (args.length > 0) {
                    next(...args);
                } else {
                    await processNextMiddleware(false);
                }
            });
        } catch (error) {
            console.error('Middleware error:', error);
            next(false); // or handle the error as appropriate
        }
    };

    await store.dispatch('Loading/increase');

    try {
        await processNextMiddleware(false);
    } finally {
        await store.dispatch('Loading/finish');
    }
}

export async function callRouteMetaMiddleware(to, from, next) {
    const routeRecords = to.matched
        .map(routeRecord => {
            let middlewareEntries = [];

            if (Array.isArray(routeRecord.meta?.middleware)) {
                middlewareEntries = routeRecord.meta.middleware;
            } else if (routeRecord.meta?.middleware) {
                middlewareEntries = [routeRecord.meta.middleware];
            }

            return {
                routeRecord,
                middlewareEntries,
            };
        })
        .filter(({ middlewareEntries }) => middlewareEntries.length > 0);

    return routeRecords.reduce(async (handledPromise, { routeRecord, middlewareEntries }) => {
        const handled = await handledPromise;

        if (handled) {
            return true;
        }

        const ability = routeRecord.meta?.ability;

        if (!ability) {
            throw new Error('The middleware must provide an ability to check');
        }

        return middlewareEntries.reduce(async (entryHandledPromise, MiddlewareStep) => {
            const entryHandled = await entryHandledPromise;

            if (entryHandled) {
                return true;
            }

            try {
                const ResolvedMiddlewareStep =
                    typeof MiddlewareStep === 'function'
                        ? MiddlewareStep
                        : routeMiddleware[`/resources/js/middleware/${MiddlewareStep}.js`]?.default;

                if (!ResolvedMiddlewareStep) {
                    throw new Error(`Undefined route middleware [${MiddlewareStep}]`);
                }

                await new ResolvedMiddlewareStep(store, router).fire(to, from, ability);

                return false;
            } catch (error) {
                const errorTypes = [error.name, error.constructor?.name].filter(Boolean);

                if (errorTypes.includes('ForbiddenError') || errorTypes.includes('GateAccessDenied')) {
                    next(buildAccessDeniedLocation(to, from));

                    return true;
                }

                console.error('Route middleware error:', error);
                next(false);

                return true;
            }
        }, Promise.resolve(false));
    }, Promise.resolve(false));
}

const beforeEachSetup = () => {
    return async (to, from, next) => {
        setPendingNavigationRoute(to);

        // Get the matched components and resolve them
        const components = to.matched.flatMap(routeRecord => {
            return routeRecord.components.default;
        });

        // Page titles render as "<PageName> - Edverum (<institution name>)".
        // The "Edverum (...)" suffix is built server-side so the institution
        // field only stores the customer-specific value.
        const titleSuffix = appTitleSuffix();
        if (typeof to.meta.title === 'undefined') {
            document.title = titleSuffix;
        } else {
            document.title = `${to.meta.title} - ${titleSuffix}`;
        }

        if (components.length === 0) {
            return next();
        }

        if (router.skipMiddleware === true) {
            return next();
        }

        // Get the middleware for all the matched components.
        const componentMiddleware = await getMiddleware(components);

        let middlewareNavigationHandled = false;

        // Call each middleware
        await callMiddleware(componentMiddleware, to, from, (...args) => {
            middlewareNavigationHandled = true;
            return next(...args);
        });

        if (!middlewareNavigationHandled) {
            middlewareNavigationHandled = await callRouteMetaMiddleware(to, from, next);
        }

        if (!middlewareNavigationHandled) {
            if (components[components.length - 1].loading !== false) {
                await store.dispatch('Loading/start');
            }

            return next();
        }

        return undefined;
    };
};

export { beforeEachSetup };

export const afterEachSetup = _app => {
    /**
     * Global after hook.
     *
     * @param {import('vue-router').Route} to
     */
    return async function afterEach(to) {
        setPendingNavigationRoute(null);
        loadingService.closeAll();
    };
};

sync(store, router, { moduleName: 'Route' });

export default router;

function getBasePath(route) {
    // Assuming the parent routes are one level deep
    const pathSegments = route.path.split('/');
    return pathSegments.slice(0, 2).join('/'); // This gets the first two segments, e.g., /users from /users/1/edit
}

export function getBaseResource(route) {
    const pathSegments = route.path.split('/');
    const resourceType = pathSegments[1];
    const resourceId = pathSegments[2];
    if (resourceType && resourceId && !NON_RESOURCE_BASE_SECTIONS.includes(resourceType)) {
        return { type: resourceType, id: resourceId };
    }

    return null;
}

// Paths that share the reusable user-profile sidebar (`user-sidebar`): the
// self-profile (/me, /my/*) and any specific user opened from People
// (/people/:personId and its sub-pages). All of these render the same
// PersonView/UserView instances, which register the sidebar in created().
// Because Vue reuses those instances when navigating between them, created()
// does not fire again — so if the menu-reset guard clears the sidebar during
// such a navigation, nothing re-registers it and the menu disappears (e.g.
// opening another user from People, then going to My Profile). Treating the
// whole family as one context keeps the reset from firing within it; the
// sidebar's items are recomputed reactively per-route by UserSidebar.vue.
export function isUserProfileRoute(route) {
    const basePath = getBasePath(route);

    if (PERSONAL_PROFILE_BASE_ROUTES.includes(basePath)) {
        return true;
    }

    // /people/:personId (and its sub-pages) use the user sidebar; the bare
    // /people list page does not, so require a second segment (the person id).
    return basePath === '/people' && !!route.path.split('/')[2];
}

// eslint-disable-next-line complexity
router.beforeResolve(async (to, from, next) => {
    if (store.state.isOutOfDate && !(to.path === from.path && to.hash)) {
        window.location.href = to.fullPath;

        return false;
    }

    const toBaseResource = getBaseResource(to);
    const fromBaseResource = getBaseResource(from);
    // Reset the menu if navigating away from the current base path and the
    // navigation is not within the shared user-profile context (self profile
    // and People user pages reuse the same components + sidebar; see
    // isUserProfileRoute).
    if (
        to.path !== from.path &&
        !(isUserProfileRoute(to) && isUserProfileRoute(from)) &&
        toBaseResource?.type !== fromBaseResource?.type &&
        toBaseResource?.id !== fromBaseResource?.id
    ) {
        await store.dispatch('Menu/reset');
    }

    const userData = store.getters['User/get'];
    const userHasActiveQuiz = store.getters['User/hasActiveQuiz'];
    const userHasQuizData = !!userData('quiz_id') && !!userData('quiz_activity');

    let quizAttempt = null;

    // noinspection UnusedCatchParameterJS
    try {
        quizAttempt = store.state.QuizAttempt[userData('quiz_id')]?.quiz_attempt;
    } catch (_error) {
        /* empty */
    }

    const { automatedSurveyAssignmentReady } = store.state;
    const isNavigatingWithinSameRoute = to.path === from.path && to.hash;

    // Don't interfere if user has an active quiz - let CheckQuizAttempt middleware handle regular quiz forcing
    if (userHasActiveQuiz) {
        return next();
    }

    // For automated surveys, check if user is ready to be forced into them
    // Only proceed if:
    // 1. There's an automated survey assignment ready
    // 2. Not navigating within same route
    // 3. User either has no quiz data OR their quiz attempt is completed
    const shouldNavigateToSurvey =
        automatedSurveyAssignmentReady && !isNavigatingWithinSameRoute && (!userHasQuizData || quizAttempt?.end_time);

    if (shouldNavigateToSurvey) {
        const assignmentRoute = await routeToAutomatedSurveyAssignment(automatedSurveyAssignmentReady);

        if (assignmentRoute.name) {
            const resolvedRoute = router.resolve(assignmentRoute);

            window.location.href = resolvedRoute?.route.fullPath;

            return false;
        }
    }

    // return middleware.run(to, from).then(() => next());
    return next();
});
